import React from "react";
import { Box, Typography } from "@mui/material";
import { LuLayers } from "react-icons/lu";
import { renderCurrencyIconColored } from "../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import FloatingField from "./FloatingField";

// Read-only amount cell with the currency icon, styled like the other
// floating-label fields so the row reads as one form.
const AmountField = ({ amount }) => (
    <Box sx={{ position: "relative", width: "100%" }}>
        <Box
            component="label"
            sx={{
                position: "absolute",
                left: "14px",
                top: "-8px",
                fontSize: "12px",
                color: "#949daa",
                backgroundColor: "#fff",
                padding: "0 4px",
                zIndex: 1,
                fontFamily: "Inter Tight",
                lineHeight: "16px",
            }}
        >
            Amount
        </Box>
        <Box
            sx={{
                height: "52px",
                borderRadius: "12px",
                border: "1px solid #d1d5db",
                backgroundColor: "#f9fafb",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "0 16px",
                fontSize: "14px",
                color: "#0d0d0e",
                fontFamily: "Inter Tight",
            }}
        >
            {amount != null ? (
                <>
                    {renderCurrencyIconColored("black-small")}
                    {formattedAmountWithoutAed(amount)}
                </>
            ) : (
                "-"
            )}
        </Box>
    </Box>
);

// Tab 03 — read-only payment breakdown. The split comes from the design
// scope; the contractor only reviews it. Amounts are computed on the
// frontend from the proposal amount and each row's percentage.
export default function DesignPayments({ paymentRows = [], proposalAmount }) {
    const totalAmount = Number(proposalAmount) || 0;
    const total = paymentRows.reduce(
        (sum, row) => sum + (Number(row.percentage) || 0),
        0,
    );
    const totalValid = paymentRows.length === 0 || total === 100;

    return (
        <Box
            sx={{
                width: "100%",
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
                backgroundColor: "#fff",
                padding: { xs: "16px", md: "24px" },
                margin: "1vh 0",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "24px",
                }}
            >
                <LuLayers size={20} color="#4D6BF6" />
                <Typography
                    sx={{
                        fontSize: "16px",
                        fontWeight: 500,
                        color: "#0d0d0e",
                        fontFamily: "Inter Tight",
                    }}
                >
                    Payment
                </Typography>
            </Box>

            {paymentRows.length === 0 && (
                <Typography
                    sx={{
                        fontSize: "13px",
                        color: "#949daa",
                        fontFamily: "Inter Tight",
                    }}
                >
                    No payment structure defined for this design scope.
                </Typography>
            )}

            <Box
                sx={{ display: "flex", flexDirection: "column", gap: "18px" }}
            >
                {paymentRows.map((row, idx) => {
                    const amount = totalAmount
                        ? (totalAmount * (Number(row.percentage) || 0)) / 100
                        : null;
                    return (
                        <Box
                            // eslint-disable-next-line react/no-array-index-key
                            key={row.group_id || idx}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    md: "2fr 1fr 1fr",
                                },
                                alignItems: "start",
                                gap: "12px",
                            }}
                        >
                            <FloatingField
                                label="Payment Name"
                                value={row.name ?? ""}
                                disabled
                            />
                            <FloatingField
                                label="Percentage (%)"
                                value={row.percentage ?? ""}
                                disabled
                            />
                            <AmountField amount={amount} />
                        </Box>
                    );
                })}
            </Box>

            {paymentRows.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "20px",
                        paddingTop: "16px",
                        borderTop: "1px solid #F3F4F6",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#0d0d0e",
                            fontFamily: "Inter Tight",
                        }}
                    >
                        Total
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                        }}
                    >
                        {totalAmount > 0 && (
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#0d0d0e",
                                    fontFamily: "Inter Tight",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                }}
                            >
                                {renderCurrencyIconColored("black-small")}
                                {formattedAmountWithoutAed(totalAmount)}
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: totalValid ? "#16A34A" : "#EF4444",
                                fontFamily: "Inter Tight",
                            }}
                        >
                            {total}%
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
